import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Package, FileText, Download } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../context/AuthContext";
import { FrequentlyReordered } from "../components/FrequentlyReordered";
import MySubscriptions from "../components/MySubscriptions";

export default function AccountPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${API}/orders`);
        setOrders(res.data);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchOrders();
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">Please sign in to view your account</p>
        <Link to="/login" className="btn-primary mt-4 inline-block">Sign In</Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">My Account</h1>

      <div className="mb-8">
        <FrequentlyReordered variant="card" limit={6} />
      </div>

      <div className="mb-8">
        <MySubscriptions />
      </div>

      <div className="grid md:grid-cols-3 gap-8">
        <div className="card p-6" data-testid="account-info">
          <h3 className="font-semibold mb-4">Account Details</h3>
          <p className="text-gray-600">{user.name}</p>
          <p className="text-gray-600">{user.email}</p>
          <span className={`inline-block mt-2 px-3 py-1 rounded text-sm ${user.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
            {user.role}
          </span>
        </div>

        <div className="md:col-span-2">
          <h3 className="font-semibold mb-4">Order History</h3>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="card p-6 text-center">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No orders yet</p>
              <Link to="/products" className="text-[#00a99d] hover:underline text-sm">Start shopping</Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="orders-list">
              {orders.map((order) => (
                <div key={order.id} className="card p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-medium">Order #{order.id.slice(0, 8)}</p>
                      <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                    </div>
                    <span className={`badge status-${order.status}`}>{order.status}</span>
                  </div>
                  <div className="divide-y">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 py-2">
                        <img src={item.image_url || "https://via.placeholder.com/40"} alt="" className="w-10 h-10 object-contain rounded" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.name}</p>
                          <p className="text-xs text-gray-500">Qty: {item.quantity}</p>
                        </div>
                        <p className="text-sm font-semibold">&pound;{(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t flex justify-between items-center">
                    <div className="flex items-center gap-3">
                      <p className="text-sm text-gray-500">{order.items.length} item(s)</p>
                      <a
                        href={`${API}/orders/${order.id}/invoice`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-[#00a99d] hover:underline font-medium"
                        data-testid={`download-invoice-${order.id}`}
                        onClick={async (e) => {
                          e.preventDefault();
                          try {
                            const res = await axios.get(`${API}/orders/${order.id}/invoice`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `Carepoint-Invoice-${order.id.slice(0,8).toUpperCase()}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (err) {
                            console.error('Failed to download invoice:', err);
                          }
                        }}
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Download Invoice
                      </a>
                    </div>
                    <p className="font-bold text-lg">Total: &pound;{order.total.toFixed(2)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
